/**
 * My Licenses Page Component
 * 
 * Shows all jobs/content the user has access to
 * Includes owned jobs, requested access, and purchases (future)
 */

import React, { useState, useEffect } from 'react';
import { getUserGrants, createAccessGrant, revokeAccessGrant } from '../../utils/accessGrantHelpers';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import LicenseCard from './LicenseCard';
import './MyLicensesPage.css';

const MyLicensesPage = ({ user }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'owner' | 'request_approved' | 'purchase'
  const [creatingTest, setCreatingTest] = useState(false);
  const [viewMode, setViewMode] = useState('my-access'); // 'my-access' | 'licensed-to-others'
  const [grantedToOthers, setGrantedToOthers] = useState([]);
  const [grantedLoading, setGrantedLoading] = useState(false);
  const [sortBy, setSortBy] = useState('date-desc'); // 'date-desc' | 'date-asc' | 'model-asc' | 'model-desc'
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchLicenses = async () => {
      try {
        // Get all active grants for this user
        const grants = await getUserGrants(user.uid);
        
        // Fetch job details for each grant
        const licensesWithDetails = await Promise.all(
          grants.map(async (grant) => {
            try {
              const jobDoc = await getDoc(doc(db, 'jobs', grant.jobId));
              if (jobDoc.exists()) {
                return {
                  ...grant,
                  job: { id: jobDoc.id, ...jobDoc.data() }
                };
              }
              return null;
            } catch (err) {
              console.error('Error fetching job:', err);
              return null;
            }
          })
        );

        // Filter out null entries
        const validLicenses = licensesWithDetails.filter(l => l !== null);
        setLicenses(validLicenses);

      } catch (error) {
        console.error('Error fetching licenses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLicenses();
  }, [user, navigate]);

  // Fetch grants you've given to others
  useEffect(() => {
    if (!user || viewMode !== 'licensed-to-others') return;

    const fetchGrantedToOthers = async () => {
      setGrantedLoading(true);
      try {
        // Get all jobs owned by this user
        const jobsQuery = query(
          collection(db, 'jobs'),
          where('userId', '==', user.uid)
        );
        
        const jobsSnapshot = await getDocs(jobsQuery);
        const jobIds = jobsSnapshot.docs.map(doc => doc.id);
        
        if (jobIds.length === 0) {
          setGrantedLoading(false);
          return;
        }

        // Get grants for these jobs (max 10 at a time due to Firestore limit)
        const allGrants = [];
        for (let i = 0; i < jobIds.length; i += 10) {
          const batchIds = jobIds.slice(i, i + 10);
          
          const grantsQuery = query(
            collection(db, 'accessGrants'),
            where('jobId', 'in', batchIds),
            where('isActive', '==', true)
          );
          
          const grantsSnapshot = await getDocs(grantsQuery);
          
          grantsSnapshot.forEach(doc => {
            const data = doc.data();
            // Only show grants to OTHER users (not self)
            if (data.userId !== user.uid) {
              allGrants.push({
                id: doc.id,
                ...data
              });
            }
          });
        }

        // Fetch job details for each grant
        const grantsWithDetails = await Promise.all(
          allGrants.map(async (grant) => {
            try {
              const jobDoc = await getDoc(doc(db, 'jobs', grant.jobId));
              if (jobDoc.exists()) {
                return {
                  ...grant,
                  job: { id: jobDoc.id, ...jobDoc.data() }
                };
              }
              return grant;
            } catch (err) {
              console.error('Error fetching job:', err);
              return grant;
            }
          })
        );

        setGrantedToOthers(grantsWithDetails);
      } catch (error) {
        console.error('Error fetching granted to others:', error);
      } finally {
        setGrantedLoading(false);
      }
    };

    fetchGrantedToOthers();
  }, [user, viewMode]);

  const filteredLicenses = filter === 'all' 
    ? licenses 
    : licenses.filter(l => l.grantType === filter);

  // Sort licenses
  const sortedLicenses = [...filteredLicenses].sort((a, b) => {
    switch (sortBy) {
      case 'date-desc':
        return (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0);
      case 'date-asc':
        return (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0);
      case 'model-asc':
        return (a.job?.selectedModel || '').localeCompare(b.job?.selectedModel || '');
      case 'model-desc':
        return (b.job?.selectedModel || '').localeCompare(a.job?.selectedModel || '');
      default:
        return 0;
    }
  });

  // Sort granted to others
  const sortedGrantedToOthers = [...grantedToOthers].sort((a, b) => {
    switch (sortBy) {
      case 'date-desc':
        return (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0);
      case 'date-asc':
        return (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0);
      case 'model-asc':
        return (a.job?.selectedModel || '').localeCompare(b.job?.selectedModel || '');
      case 'model-desc':
        return (b.job?.selectedModel || '').localeCompare(a.job?.selectedModel || '');
      default:
        return 0;
    }
  });

  const handleViewLicense = (license) => {
    navigate(`/licenses/${license.id}`);
  };

  const handleRevokeGrant = async (grant) => {
    if (!window.confirm(`Revoke access for ${grant.requesterName || 'this user'}? They will immediately lose access.`)) {
      return;
    }

    try {
      await revokeAccessGrant(grant.id, user.uid, 'Revoked by owner from License Hub');
      
      // Remove from list
      setGrantedToOthers(prev => prev.filter(g => g.id !== grant.id));
      
      alert('✅ Access revoked successfully!');
    } catch (err) {
      console.error('Error revoking access:', err);
      alert(err.message || 'Failed to revoke access');
    }
  };

  const handleCreateTestGrants = async () => {
    if (!user) return;
    
    setCreatingTest(true);
    
    try {
      // Get all jobs owned by this user
      const jobsQuery = query(
        collection(db, 'jobs'),
        where('userId', '==', user.uid)
      );
      
      const jobsSnapshot = await getDocs(jobsQuery);
      
      if (jobsSnapshot.empty) {
        alert('No jobs found. Create a job first (run an AI analysis).');
        setCreatingTest(false);
        return;
      }
      
      let createdCount = 0;
      
      // Create grants for all user's jobs
      for (const jobDoc of jobsSnapshot.docs) {
        const jobData = jobDoc.data();
        
        try {
          await createAccessGrant({
            userId: user.uid,
            jobId: jobDoc.id,
            evidenceId: jobData.evidenceId,
            casefileId: jobData.casefileId,
            grantType: 'owner',
            casefileName: 'Your Job',
            casefileType: 'private',
            showCasefileName: false
          });
          createdCount++;
        } catch (err) {
          console.log(`Skipped job ${jobDoc.id} (may already have grant)`);
        }
      }
      
      alert(`✅ Created ${createdCount} test grants! Refresh the page.`);
      
      // Refresh the page
      window.location.reload();
      
    } catch (error) {
      console.error('Error creating test grants:', error);
      alert('Failed to create test grants: ' + error.message);
    } finally {
      setCreatingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="licenses-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your licenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="licenses-page">
      {/* Header */}
      <div className="licenses-header">
        <div className="header-content">
          <h1>📜 License Hub</h1>
          <p className="header-subtitle">
            Manage all your access and licenses
          </p>
        </div>
        
        {/* Test Button - Only show if no licenses */}
        {licenses.length === 0 && viewMode === 'my-access' && (
          <button 
            className="test-button"
            onClick={handleCreateTestGrants}
            disabled={creatingTest}
          >
            {creatingTest ? '⏳ Creating...' : '🧪 Create Test Grants'}
          </button>
        )}
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button
          className={`mode-btn ${viewMode === 'my-access' ? 'active' : ''}`}
          onClick={() => setViewMode('my-access')}
        >
          📥 My Access
        </button>
        <button
          className={`mode-btn ${viewMode === 'licensed-to-others' ? 'active' : ''}`}
          onClick={() => setViewMode('licensed-to-others')}
        >
          📤 Licensed to Others
        </button>
      </div>

      {/* My Access View */}
      {viewMode === 'my-access' && (
        <>
          {/* Filters and Sort */}
          <div className="filters-and-sort">
            <div className="licenses-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({licenses.length})
        </button>
        <button
          className={`filter-btn ${filter === 'owner' ? 'active' : ''}`}
          onClick={() => setFilter('owner')}
        >
          👤 Owned ({licenses.filter(l => l.grantType === 'owner').length})
        </button>
        <button
          className={`filter-btn ${filter === 'request_approved' ? 'active' : ''}`}
          onClick={() => setFilter('request_approved')}
        >
          ✅ Granted ({licenses.filter(l => l.grantType === 'request_approved').length})
        </button>
        <button
          className={`filter-btn ${filter === 'purchase' ? 'active' : ''}`}
          onClick={() => setFilter('purchase')}
        >
          💳 Purchased ({licenses.filter(l => l.grantType === 'purchase').length})
        </button>
      </div>

            {/* Sort Dropdown */}
            <div className="sort-control">
              <label htmlFor="sort-select">Sort:</label>
              <select 
                id="sort-select"
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="date-desc">📅 Newest First</option>
                <option value="date-asc">📅 Oldest First</option>
                <option value="model-asc">🔤 Model A-Z</option>
                <option value="model-desc">🔤 Model Z-A</option>
              </select>
            </div>
          </div>

      {/* Licenses Grid */}
      {sortedLicenses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No licenses found</h3>
          <p>
            {filter === 'all'
              ? "You don't have any licenses yet. Create a job or request access to get started."
              : `No ${filter} licenses found. Try a different filter.`}
          </p>
        </div>
      ) : (
        <div className="licenses-grid">
          {sortedLicenses.map(license => (
            <LicenseCard
              key={license.id}
              license={license}
              onViewContent={handleViewLicense}
            />
          ))}
        </div>
      )}
        </>
      )}

      {/* Licensed to Others View */}
      {viewMode === 'licensed-to-others' && (
        <div className="granted-to-others-section">
          {/* Sort Control for Granted */}
          {grantedToOthers.length > 0 && (
            <div className="sort-control-standalone">
              <label htmlFor="granted-sort-select">Sort:</label>
              <select 
                id="granted-sort-select"
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="date-desc">📅 Newest First</option>
                <option value="date-asc">📅 Oldest First</option>
                <option value="model-asc">🔤 Model A-Z</option>
                <option value="model-desc">🔤 Model Z-A</option>
              </select>
            </div>
          )}

          {grantedLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading granted licenses...</p>
            </div>
          ) : grantedToOthers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>No Licenses Granted to Others</h3>
              <p>You haven't granted access to anyone yet.</p>
              <p className="help-text">When users request access and you approve, they'll appear here.</p>
            </div>
          ) : (
            <div className="granted-grid">
              {sortedGrantedToOthers.map(grant => (
                <div key={grant.id} className="granted-card">
                  {/* User Badge */}
                  <div className={`granted-badge ${grant.grantType === 'request_approved' ? 'revocable' : 'permanent'}`}>
                    {grant.revocable ? '✅ Revocable' : '💳 Purchased'}
                  </div>

                  {/* User Info */}
                  <div className="granted-user-info">
                    <h3>{grant.requesterName || 'Anonymous User'}</h3>
                    <p className="user-email">{grant.requesterEmail}</p>
                  </div>

                  {/* Grant Details */}
                  <div className="granted-meta">
                    <div className="meta-item">
                      <strong>Job:</strong> {grant.job?.selectedModel || 'AI Analysis'}
                    </div>
                    <div className="meta-item">
                      <strong>Granted:</strong> {grant.createdAt?.toDate().toLocaleDateString()}
                    </div>
                    <div className="meta-item">
                      <strong>Job ID:</strong> #{grant.jobId?.slice(-8)}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="granted-actions">
                    <button
                      onClick={() => navigate(`/licenses/${grant.id}`)}
                      className="view-grant-btn"
                    >
                      👁️ View Details
                    </button>
                    {grant.revocable && (
                      <button
                        onClick={() => handleRevokeGrant(grant)}
                        className="revoke-grant-btn"
                      >
                        🗑️ Revoke
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyLicensesPage;

